<script setup lang="ts">
import { money } from '@nuts/auto-delivery-sdk/dist/utils/format';
import { dollars } from '@nuts/auto-delivery-sdk/dist/utils/money';
import { useVModel } from '@vueuse/core';
import { computed, toRef } from 'vue';

import Select from '@/components/base/form/OutlinedSelect.vue';
import VariantOption from '@/components/pdp/buy-block/VariantOption.vue';
import WholesaleSavingsBadge from '@/components/pdp/buy-block/WholesaleSavingsBadge.vue';
import { useProductDetail } from '@/composables/useProductDetail';
import {
  isIndividuallyWrapped,
  NutsProduct,
  priceForQuantity,
  PurchaseOptions,
} from '@/utils/product';

const props = defineProps<{
  modelValue: PurchaseOptions;
  product: NutsProduct;
  mode?: 'singleLine';
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: PurchaseOptions): void;
}>();

const { isGiftCertificate } = useProductDetail(toRef(props, 'product'));

const selections = useVModel(props, 'modelValue', emit);

const selectedVariantGroup = computed(() => {
  const isSelected = (variations: { id: number; sku: string }) =>
    isGiftCertificate.value
      ? variations.id === selections.value.variation
      : variations.sku === selections.value.sku;
  return (
    props.product.variantGroups.find(
      (g) => isSelected(g) || g.otherVariations?.some((o) => o.variations.find(isSelected)),
    ) ?? props.product.variantGroups[0]
  );
});

const selectedVariant = computed(
  () =>
    props.product.variants.find((v) =>
      isGiftCertificate.value
        ? v.id === selections.value.variation
        : v.sku === selections.value.sku,
    ) ?? props.product.variants[0],
);

const variationsOptions = computed(
  () =>
    selectedVariantGroup.value?.otherVariations?.map((variation) =>
      variation.variations.map((option) => ({
        label: option[variation.key]?.label.en ?? '',
        value: isGiftCertificate.value ? option.id : option.sku,
      })),
    ) ?? [],
);

const hasLongVariant = computed(() =>
  props.product.variantGroups.some((v) => (v.unitName?.length || 0) > 23),
);
</script>

<template>
  <div>
    <fieldset>
      <legend class="items-center text-xs sm:text-sm" data-test="gift-value">
        <strong>
          {{ isGiftCertificate ? 'Value:' : 'Size:' }}
        </strong>
        <span class="ml-1 text-neutral-600">
          {{
            isGiftCertificate ? money(selectedVariant.prices[0].value) : selectedVariant.unitName
          }}
        </span>
        <span
          v-if="selectedVariant.bulk || selectedVariant.wholesale"
          class="inline-flex ml-1 text-neutral-600"
        >
          (
          <strong>
            {{ money(priceForQuantity(selectedVariant, { quantity: selections.quantity }, true)) }}
          </strong>
          /lb)
        </span>
        <span
          v-if="!isIndividuallyWrapped(selectedVariant)"
          class="px-1 ml-2 text-black rounded bg-neutral-200"
        >
          Not individually packaged
        </span>
      </legend>
      <div v-if="isGiftCertificate" class="grid grid-cols-4 gap-2 mt-2">
        <VariantOption
          v-for="group in product.variantGroups"
          v-model="selections.variation"
          :key="group.id"
          data-promo="1"
          :data-promo-creative="`Value: ${dollars(group.prices[0].value)}`"
          data-promo-name="Gift certificate selector"
          isGiftCertificate
          :isSelected="group.id === selectedVariantGroup.id"
          :variant="group"
        />
      </div>
      <div
        v-else
        class="grid grid-cols-2 gap-2 mt-2"
        :class="{
          'xl:grid-cols-3': !hasLongVariant && !mode,
          'grid-cols-4': mode === 'singleLine',
        }"
        data-test="variant-size-selector"
      >
        <VariantOption
          v-for="group in product.variantGroups"
          v-model="selections.sku"
          :key="group.id"
          data-promo="1"
          :data-promo-creative="`Variant: ${group.unitName}`"
          data-promo-name="Variant selector"
          :isSelected="group.id === selectedVariantGroup.id"
          :selections="selections"
          :variant="group"
          :mode="mode"
        >
          <template #badge>
            <WholesaleSavingsBadge
              v-if="!mode && group.wholesale"
              class="absolute top-1 right-1"
              :isSelected="group.id === selectedVariantGroup.id"
              :product="product"
              :variant="
                group.otherVariations?.[0]?.variations.find((v) => v.sku === selectedVariant.sku) ??
                group
              "
            />
          </template>
        </VariantOption>
      </div>
    </fieldset>
    <template v-if="selectedVariantGroup">
      <fieldset
        v-for="(variation, index) in selectedVariantGroup.otherVariations"
        class="mt-4"
        :key="variation.key"
      >
        <label :for="`variation-select-${variation.key}`" class="m-0">
          <legend class="text-xs sm:text-sm" data-test="grind-size-label">
            <strong> {{ variation.label }}: </strong>
            <span class="ml-1 font-normal text-neutral-600">
              {{ selectedVariant[variation.key]?.label.en }}
            </span>
          </legend>
          <Select
            v-model="selections.sku"
            class="mt-2 font-semibold rounded-md customization-select"
            data-test="grind-size-select"
            data-promo="1"
            :data-promo-creative="`${variation.label}: ${selectedVariant[variation.key]?.label.en}`"
            :data-promo-name="`${variation.label} option`"
            :id="`variation-select-${variation.key}`"
            :options="variationsOptions[index]"
          />
        </label>
      </fieldset>
    </template>
  </div>
</template>

<style scoped>
.customization-select {
  @apply bg-neutral-100 border-black;
}
</style>
